"use client";

import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";

const caveat = Caveat({
  subsets: ["latin"],
  weight: ["700"],
  variable: "--font-caveat",
});

/* ─────────────────────────────────────────────────────────────
   1. ARCHITECTURE FIRM: SMART PROJECT MANAGEMENT SECTION
   Exact reproduction of Odoo's Architecture Project & Tasks Kanban UI
   ───────────────────────────────────────────────────────────── */
export function ArchitectureSmartProjectManagementSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-4 lg:gap-6 items-center">

          {/* LEFT COLUMN: ODOO TASKS KANBAN MOCKUP CARD */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 relative w-full flex justify-center lg:justify-start"
          >
            {/* White Window Container */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 text-slate-800 text-[10px] sm:text-[11.5px] w-full max-w-5xl p-3 sm:p-4 font-sans">

              {/* Odoo Top Header Navigation Tabs */}
              <div className="border-b border-slate-100 pb-2 mb-2 space-y-1.5">
                <div className="flex flex-wrap items-center gap-1 text-[8.5px] font-medium text-slate-600">
                  <span className="bg-emerald-50 text-emerald-700 font-bold border border-emerald-200 px-2 py-0.5 rounded cursor-pointer">Tasks</span>
                  <span className="cursor-pointer hover:text-slate-900 px-1">Timesheets</span>
                  <span className="cursor-pointer hover:text-slate-900 px-1">Documents</span>
                  <span className="cursor-pointer hover:text-slate-900 px-1">Sales Orders</span>
                  <span className="cursor-pointer hover:text-slate-900 px-1">Invoices</span>
                  <span className="cursor-pointer hover:text-slate-900 px-1">Vendor Bills</span>
                  <span className="cursor-pointer hover:text-slate-900 px-1">Expenses</span>
                  <span className="cursor-pointer hover:text-slate-900 px-1">Dashboard</span>
                </div>

                {/* Sub Header / Breadcrumb & Search */}
                <div className="flex flex-wrap items-center justify-between gap-1 pt-1 text-[8.5px]">
                  <div className="flex items-center gap-1">
                    <span className="bg-[#7C5A76] text-white px-1.5 py-0.2 rounded font-semibold text-[8px]">New</span>
                    <span className="text-slate-500 font-medium truncate max-w-[180px]">Projects / <strong className="text-slate-800">S00006 - Architectura...</strong> ⚙️</span>
                  </div>
                  <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-[8px] text-slate-500">
                    <span>🔍</span>
                    <span className="bg-purple-100 text-purple-800 px-1 rounded font-semibold">Open Tasks ✖</span>
                    <span className="bg-emerald-100 text-emerald-800 px-1 rounded font-semibold">Stage ✖</span>
                  </div>
                </div>
              </div>

              {/* Tasks Kanban Columns (4 columns) */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[8px]">

                {/* Column 1: New (4) */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-[9px] font-bold text-slate-800 border-b border-slate-100 pb-1">
                    <span>New</span>
                    <div className="flex items-center gap-0.5">
                      <span className="text-slate-400 font-normal">+</span>
                      <span className="text-slate-500 font-semibold text-[8px]">4</span>
                    </div>
                  </div>
                  <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden flex">
                    <div className="w-2/3 bg-emerald-500 h-full" />
                    <div className="w-1/3 bg-sky-400 h-full" />
                  </div>

                  {/* Card 1 */}
                  <div className="bg-white border border-slate-200 rounded p-1.5 shadow-sm space-y-1">
                    <strong className="text-slate-900 text-[8.5px] block font-bold">Worksite follow-up</strong>
                    <span className="text-slate-400 text-[7.5px] block">Cool company , Joanna Davis</span>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="bg-sky-50 text-sky-700 border border-sky-200 text-[7px] px-1 rounded font-semibold">Onsite</span>
                    </div>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[7.5px]">
                      <span className="text-amber-400">⭐ <span className="text-slate-400">🕒 08:00</span></span>
                      <span className="text-slate-400">⌛</span>
                    </div>
                  </div>

                  {/* Card 2 */}
                  <div className="bg-white border border-slate-200 rounded p-1.5 shadow-sm space-y-1">
                    <strong className="text-slate-900 text-[8.5px] block font-bold">Materials Selection</strong>
                    <span className="text-slate-400 text-[7.5px] block">Cool company , Joanna Davis</span>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="bg-amber-50 text-amber-700 border border-amber-200 text-[7px] px-1 rounded font-semibold">Procurement</span>
                    </div>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[7.5px]">
                      <span className="text-slate-300">☆</span>
                      <span className="w-3 h-3 rounded bg-purple-700 text-white font-bold text-[7px] flex items-center justify-center">A</span>
                    </div>
                  </div>

                  {/* Card 3 */}
                  <div className="bg-white border border-slate-200 rounded p-1.5 shadow-sm space-y-1">
                    <strong className="text-slate-900 text-[8.5px] block font-bold">Execution Study</strong>
                    <span className="text-slate-400 text-[7.5px] block">Cool company , Joanna Davis</span>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[7px] px-1 rounded font-semibold">Admin</span>
                      <span className="bg-rose-50 text-rose-700 border border-rose-200 text-[7px] px-1 rounded font-semibold">Design</span>
                    </div>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[7.5px]">
                      <span className="text-slate-300">☆ <span className="text-slate-400">🕒 08:00</span></span>
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                    </div>
                  </div>
                </div>

                {/* Column 2: Assessment (2) */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-[9px] font-bold text-slate-800 border-b border-slate-100 pb-1">
                    <span>Assessment</span>
                    <div className="flex items-center gap-0.5">
                      <span className="text-slate-400 font-normal">+</span>
                      <span className="text-slate-500 font-semibold text-[8px]">2</span>
                    </div>
                  </div>
                  <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden flex">
                    <div className="w-full bg-amber-400 h-full" />
                  </div>

                  {/* Card 1 */}
                  <div className="bg-white border border-slate-200 rounded p-1.5 shadow-sm space-y-1">
                    <strong className="text-slate-900 text-[8.5px] block font-bold">3D Images</strong>
                    <span className="text-slate-400 text-[7.5px] block">Cool company , Joanna Davis</span>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="bg-rose-50 text-rose-700 border border-rose-200 text-[7px] px-1 rounded font-semibold">Design</span>
                    </div>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[7.5px]">
                      <span className="text-amber-400">⭐ <span className="text-slate-400">🕒 03:00</span></span>
                      <span className="text-slate-400">⌛</span>
                    </div>
                  </div>

                  {/* Card 2: Floor Plan with Architectural Blueprint Graphic */}
                  <div className="bg-white border border-slate-200 rounded p-1.5 shadow-sm space-y-1">
                    <strong className="text-slate-900 text-[8.5px] block font-bold">Floor Plan</strong>
                    <span className="text-slate-400 text-[7.5px] block">Cool company , Joanna Davis</span>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="bg-rose-50 text-rose-700 border border-rose-200 text-[7px] px-1 rounded font-semibold">Design</span>
                    </div>

                    {/* CAD Floor Plan Blueprint Image */}
                    <div className="my-1 border border-slate-200 rounded overflow-hidden bg-slate-50 p-1 flex justify-center">
                      <svg className="w-full h-12 text-slate-700" viewBox="0 0 100 60" fill="none" stroke="currentColor" strokeWidth="1">
                        <rect x="5" y="5" width="90" height="50" strokeWidth="1.5" />
                        <line x1="35" y1="5" x2="35" y2="55" />
                        <line x1="65" y1="5" x2="65" y2="55" />
                        <line x1="5" y1="30" x2="95" y2="30" />
                        <rect x="12" y="10" width="15" height="15" strokeDasharray="2,2" />
                        <rect x="42" y="10" width="15" height="15" strokeDasharray="2,2" />
                        <rect x="72" y="10" width="15" height="15" strokeDasharray="2,2" />
                        <rect x="12" y="35" width="15" height="15" />
                        <rect x="42" y="35" width="15" height="15" />
                        <rect x="72" y="35" width="15" height="15" />
                      </svg>
                    </div>

                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[7.5px]">
                      <span className="text-amber-400">⭐ <span className="text-slate-400">🕒 09:00</span></span>
                      <div className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded bg-purple-700 text-white font-bold text-[7px] flex items-center justify-center">A</span>
                        <span className="text-amber-500 font-bold">⚠️</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Column 3: Ongoing (2) */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-[9px] font-bold text-slate-800 border-b border-slate-100 pb-1">
                    <span>Ongoing</span>
                    <div className="flex items-center gap-0.5">
                      <span className="text-slate-400 font-normal">+</span>
                      <span className="text-slate-500 font-semibold text-[8px]">2</span>
                    </div>
                  </div>
                  <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden flex">
                    <div className="w-2/3 bg-emerald-500 h-full" />
                    <div className="w-1/3 bg-amber-400 h-full" />
                  </div>

                  {/* Card 1 */}
                  <div className="bg-white border border-slate-200 rounded p-1.5 shadow-sm space-y-1">
                    <strong className="text-slate-900 text-[8.5px] block font-bold">Building Permit</strong>
                    <span className="text-slate-400 text-[7.5px] block">Cool company , Joanna Davis</span>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[7px] px-1 rounded font-semibold">Admin</span>
                    </div>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[7.5px]">
                      <span className="text-amber-400">⭐ <span className="text-slate-400">📥 0/3 🕒 01:00</span></span>
                      <span className="text-amber-500 font-bold">⚠️</span>
                    </div>
                  </div>

                  {/* Card 2 */}
                  <div className="bg-white border border-slate-200 rounded p-1.5 shadow-sm space-y-1">
                    <strong className="text-slate-900 text-[8.5px] block font-bold">Site Analysis</strong>
                    <span className="text-slate-400 text-[7.5px] block">Cool company , Joanna Davis</span>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[7px] px-1 rounded font-semibold">Admin</span>
                      <span className="bg-sky-50 text-sky-700 border border-sky-200 text-[7px] px-1 rounded font-semibold">Onsite</span>
                    </div>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[7.5px]">
                      <span className="text-slate-300">☆</span>
                      <div className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded bg-purple-700 text-white font-bold text-[7px] flex items-center justify-center">A</span>
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                      </div>
                    </div>
                  </div>

                  {/* Card 3 */}
                  <div className="bg-white border border-slate-200 rounded p-1.5 shadow-sm space-y-1">
                    <strong className="text-slate-900 text-[8.5px] block font-bold">Feasibility Study</strong>
                    <span className="text-slate-400 text-[7.5px] block">Cool company , Joanna Davis</span>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[7px] px-1 rounded font-semibold">Admin</span>
                    </div>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[7.5px]">
                      <span className="text-slate-300">☆ <span className="text-slate-400">🕒 04:00</span></span>
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                    </div>
                  </div>
                </div>

                {/* Column 4: Customer feedback (4) */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-[9px] font-bold text-slate-800 border-b border-slate-100 pb-1">
                    <span>Customer feedback</span>
                    <div className="flex items-center gap-0.5">
                      <span className="text-slate-400 font-normal">+</span>
                      <span className="text-slate-500 font-semibold text-[8px]">4</span>
                    </div>
                  </div>
                  <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden flex">
                    <div className="w-1/2 bg-sky-400 h-full" />
                  </div>

                  {/* Card 1 */}
                  <div className="bg-white border border-slate-200 rounded p-1.5 shadow-sm space-y-1">
                    <strong className="text-slate-900 text-[8.5px] block font-bold">Concept development</strong>
                    <span className="text-slate-400 text-[7.5px] block">Cool company , Joanna Davis</span>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[7px] px-1 rounded font-semibold">Admin</span>
                      <span className="bg-rose-50 text-rose-700 border border-rose-200 text-[7px] px-1 rounded font-semibold">Design</span>
                    </div>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[7.5px]">
                      <span className="text-slate-300">☆</span>
                    </div>
                  </div>
                </div>

              </div>

            </div>
          </motion.div>

          {/* RIGHT COLUMN: HANDWRITTEN TITLE, HOUSE ICON & COPYWRITING */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-6 space-y-4 text-left relative"
          >
            {/* Top Pink House Accent Icon */}
            <div className="inline-block relative">
              <svg className="w-10 h-10 text-[#FF6B6B]" viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M 6 22 L 20 8 L 34 22 V 34 H 6 Z" fill="#FF6B6B" fillOpacity="0.15" />
                <rect x="15" y="24" width="10" height="10" fill="#FF6B6B" fillOpacity="0.3" />
              </svg>
            </div>

            {/* Title with Pink Oval/Underline under "Smart" */}
            <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.1] relative`}>
              <div>
                <span className="relative inline-block px-2">
                  Smart
                  <span className="absolute -inset-1 border-2 border-[#FF6B6B] rounded-[50%] -z-10 opacity-90 transform -rotate-2" />
                </span>
                <span> project</span>
              </div>
              <div>management</div>
            </h2>

            {/* Subtext Copywriting */}
            <div className="space-y-4">
              <p className="text-xl sm:text-2xl leading-relaxed text-gray-200">
                <strong className="text-white font-bold">Power of automation.</strong> Once a sales order is confirmed, projects and tasks are automatically created for easy follow-up.
              </p>
              <p className="text-xl sm:text-2xl leading-relaxed text-gray-200">
                <strong className="text-white font-bold">Centralized dashboard.</strong> Use the top bar to handle all records related to the project, from documents to vendor bills.
              </p>
              <p className="text-xl sm:text-2xl leading-relaxed text-gray-200">
                <strong className="text-white font-bold">All the features.</strong> Use tags, stages, sub-tasks, activities, and more to organize your work.
              </p>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   3. ARCHITECTURE FIRM: FINANCE MANAGEMENT TO TURN PROFIT SECTION
   Exact reproduction of Odoo's Proforma Invoice & Profitability Dashboard UI
   ───────────────────────────────────────────────────────────── */
export function ArchitectureFinanceManagementSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto max-w-[1400px]">

        {/* CENTERED HEADER WITH STACKED GREEN COINS ICON & HANDWRITTEN TITLE */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-8 sm:mb-12 text-center max-w-4xl mx-auto space-y-3"
        >
          {/* Green Coins Stack Icon */}
          <div className="inline-block relative">
            <svg className="w-12 h-12 text-emerald-400" viewBox="0 0 50 50" fill="none" stroke="currentColor" strokeWidth="2.5">
              <ellipse cx="28" cy="15" rx="14" ry="7" fill="#10B981" fillOpacity="0.8" stroke="#047857" />
              <path d="M 14 15 V 25 C 14 28.8 20.2 32 28 32 C 35.7 32 42 28.8 42 25 V 15" fill="#10B981" fillOpacity="0.8" stroke="#047857" />
              <path d="M 14 25 V 35 C 14 38.8 20.2 42 28 42 C 35.7 42 42 38.8 42 35 V 25" fill="#10B981" fillOpacity="0.8" stroke="#047857" />
              <circle cx="15" cy="30" r="10" fill="#00A09D" stroke="#047857" strokeWidth="2" />
              <text x="11.5" y="34" fill="white" fontSize="12" fontWeight="bold">$</text>
            </svg>
          </div>

          <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight`}>
            <span>Finance management to turn </span>
            <span className="relative inline-block px-1">
              profit
              <span className="absolute bottom-1 left-0 w-full h-3 bg-emerald-400 -z-10 rounded-full opacity-90" />
            </span>
          </h2>
        </motion.div>

        {/* MAIN SECTION GRID (LEFT MOCKUP CARD + RIGHT COPYWRITING) */}
        <div className="grid lg:grid-cols-12 gap-4 lg:gap-6 items-center">

          {/* LEFT COLUMN: OVERLAPPING PROFORMA INVOICE & PROFITABILITY CARD */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 relative w-full flex justify-center lg:justify-start"
          >
            <div className="relative w-full max-w-5xl min-h-[360px] sm:min-h-[400px] flex items-center justify-center">

              {/* BACKGROUND CARD: PROFORMA INVOICE SHEET */}
              <div className="absolute left-0 top-0 w-[90%] bg-white rounded-2xl shadow-xl border border-slate-200 p-3 sm:p-4 text-[7.5px] sm:text-[8.5px] text-slate-800 font-sans opacity-95">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2 mb-2">
                  <div>
                    <span className="text-[7px] font-bold text-slate-700 block">GUILLAUME ARCHITECT</span>
                    <span className="text-[6px] text-slate-400">United States</span>
                  </div>
                  <span className="text-[6.5px] text-slate-400">Cool company, Joanna Davis</span>
                </div>

                <strong className="text-slate-900 font-bold text-[9.5px] block mb-1">PROFORMA Invoice INV/2024/00002</strong>
                <div className="flex gap-4 text-slate-500 text-[7px] mb-2">
                  <span>Invoice Date: 11/20/2024</span>
                  <span>Due Date: 11/20/2024</span>
                  <span>Source: S00006</span>
                </div>

                <table className="w-full text-left text-[7px] border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100 text-slate-400">
                      <th className="pb-1">Description</th>
                      <th className="pb-1 text-right">Qty</th>
                      <th className="pb-1 text-right">Price</th>
                      <th className="pb-1 text-right">Tax</th>
                      <th className="pb-1 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50 text-slate-600">
                    <tr>
                      <td className="py-0.5">Feasibility Study</td>
                      <td className="py-0.5 text-right">4.00 Hours</td>
                      <td className="py-0.5 text-right">200.00</td>
                      <td className="py-0.5 text-right">15%</td>
                      <td className="py-0.5 text-right font-semibold">$ 800.00</td>
                    </tr>
                    <tr>
                      <td className="py-0.5">3D Images</td>
                      <td className="py-0.5 text-right">5.00 Units</td>
                      <td className="py-0.5 text-right">100.00</td>
                      <td className="py-0.5 text-right">15%</td>
                      <td className="py-0.5 text-right font-semibold">$ 500.00</td>
                    </tr>
                    <tr>
                      <td className="py-0.5">Execution Study</td>
                      <td className="py-0.5 text-right">6.00 Hours</td>
                      <td className="py-0.5 text-right">200.00</td>
                      <td className="py-0.5 text-right">15%</td>
                      <td className="py-0.5 text-right font-semibold">$ 1,200.00</td>
                    </tr>
                    <tr>
                      <td className="py-0.5">Building Permit</td>
                      <td className="py-0.5 text-right">1.00 Units</td>
                      <td className="py-0.5 text-right">430.00</td>
                      <td className="py-0.5 text-right">15%</td>
                      <td className="py-0.5 text-right font-semibold">$ 430.00</td>
                    </tr>
                    <tr>
                      <td className="py-0.5">Worksite follow-up</td>
                      <td className="py-0.5 text-right">6.00 Units</td>
                      <td className="py-0.5 text-right">80.00</td>
                      <td className="py-0.5 text-right">15%</td>
                      <td className="py-0.5 text-right font-semibold">$ 480.00</td>
                    </tr>
                    <tr>
                      <td className="py-0.5">Mileage Expense</td>
                      <td className="py-0.5 text-right">200.00 km</td>
                      <td className="py-0.5 text-right">0.90</td>
                      <td className="py-0.5 text-right">15%</td>
                      <td className="py-0.5 text-right font-semibold">$ 180.00</td>
                    </tr>
                    <tr>
                      <td className="py-0.5">Consultation 2h</td>
                      <td className="py-0.5 text-right">1.00 Hours</td>
                      <td className="py-0.5 text-right">400.00</td>
                      <td className="py-0.5 text-right">10%</td>
                      <td className="py-0.5 text-right font-semibold">$ 400.00</td>
                    </tr>
                    <tr className="bg-slate-50 text-slate-800 font-semibold">
                      <td className="py-0.5">Down Payments (re: INV/2024/00001)</td>
                      <td className="py-0.5 text-right">-1.00 units</td>
                      <td className="py-0.5 text-right">1,995.00</td>
                      <td className="py-0.5 text-right">15%</td>
                      <td className="py-0.5 text-right text-rose-600">$-1,995.00</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* FOREGROUND OVERLAPPING CARD: PROFITABILITY DASHBOARD WIDGET */}
              <div className="absolute right-0 bottom-0 w-[70%] bg-white rounded-xl shadow-2xl border border-slate-200 p-3 sm:p-4 text-[8px] sm:text-[9px] text-slate-800 z-20 space-y-2 transform translate-y-2">
                <strong className="text-slate-900 font-bold block text-[10px] border-b border-slate-100 pb-1">Profitability</strong>

                {/* Revenues Table */}
                <div className="space-y-1 text-[7.5px]">
                  <div className="flex justify-between text-slate-400 font-semibold text-[7px] border-b border-slate-50 pb-0.5">
                    <span>Revenues</span>
                    <span>Expected</span>
                    <span>To Invoice</span>
                    <span>Invoiced</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span className="text-sky-600 font-medium">▶ Timesheets (Fixed Price)</span>
                    <span>$ 3,990</span>
                    <span>$ 3,990</span>
                    <span>$ 0</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span className="text-sky-600 font-medium">Down Payments</span>
                    <span>$ 0</span>
                    <span className="text-rose-500">$-1,995</span>
                    <span>$ 1,995</span>
                  </div>
                  <div className="flex justify-between text-slate-900 font-bold border-t border-slate-100 pt-0.5">
                    <span>Total Revenues</span>
                    <span>$ 3,990</span>
                    <span>$ 1,995</span>
                    <span>$ 1,995</span>
                  </div>
                </div>

                {/* Costs Table */}
                <div className="space-y-1 text-[7.5px] pt-1">
                  <div className="flex justify-between text-slate-400 font-semibold text-[7px] border-b border-slate-50 pb-0.5">
                    <span>Costs</span>
                    <span>Expected</span>
                    <span>To Bill</span>
                    <span>Billed</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span className="text-sky-600 font-medium">Expenses</span>
                    <span className="text-rose-500">$-609</span>
                    <span>$ 0</span>
                    <span className="text-rose-500">$-609</span>
                  </div>
                  <div className="flex justify-between text-slate-900 font-bold border-t border-slate-100 pt-0.5">
                    <span>Total Costs</span>
                    <span className="text-rose-600">$-609</span>
                    <span>$ 0</span>
                    <span className="text-rose-600">$-609</span>
                  </div>
                </div>

                {/* Total Net Profit Stats */}
                <div className="pt-1 border-t border-slate-200">
                  <div className="flex justify-between items-center text-slate-900 font-bold text-[8.5px]">
                    <span>Total</span>
                    <div className="text-right space-x-3 text-[8px]">
                      <span className="text-emerald-600">$ 3,381 <span className="text-[6.5px] block font-normal">+85%</span></span>
                      <span className="text-emerald-600">$ 1,995 <span className="text-[6.5px] block font-normal">+100%</span></span>
                      <span className="text-emerald-600">$ 1,386 <span className="text-[6.5px] block font-normal">+69%</span></span>
                    </div>
                  </div>
                </div>

              </div>

            </div>
          </motion.div>

          {/* RIGHT COLUMN: SUBTEXT COPYWRITING */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-6 space-y-4 text-left relative"
          >
            <div className="space-y-4">
              <p className="text-xl sm:text-2xl leading-relaxed text-gray-200">
                <strong className="text-white font-bold">Flexible invoicing.</strong> Invoice clients based on fixed rates, time and material, and milestones. Accept upfront or incremental payments; CodeQlik adapts to your unique business needs.
              </p>
              <p className="text-xl sm:text-2xl leading-relaxed text-gray-200">
                <strong className="text-white font-bold">Analytical breakdowns</strong> on your project's dashboard to manage costs and revenues, keeping an eye on each project's profitability.
              </p>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   4. ARCHITECTURE FIRM: RESOURCE PLANNING SECTION
   Exact reproduction of Odoo's Architecture Resource & Shift Planning Grid UI
   ───────────────────────────────────────────────────────────── */
export function ArchitectureResourcePlanningSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-8 lg:px-16 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto max-w-[1400px]">

        {/* HEADER: HANDWRITTEN TITLE & UNDERLINE + SUBTEXT */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-8 sm:mb-10 text-left max-w-3xl space-y-3"
        >
          <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight`}>
            <span>Resource </span>
            <span className="relative inline-block px-1">
              planning
              <span className="absolute bottom-1 left-0 w-full h-3 bg-sky-400 -z-10 rounded-full opacity-90" />
            </span>
          </h2>

          <p className="text-xl sm:text-2xl leading-relaxed text-gray-200">
            <strong className="text-white font-bold">Centralized planning.</strong> Create shifts for colleagues as well as material resources. Use auto-planning, recurring shifts, templates, and roles to maximize efficiency.
          </p>
        </motion.div>

        {/* RESOURCE SCHEDULING GRID CARD MOCKUP */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="w-full flex items-center justify-center relative"
        >
          <div className="relative w-full max-w-5xl">

            {/* Top Right Pink Cloud Doodle Accent */}
            <div className="hidden lg:block absolute -top-8 -right-6 z-20 text-[#FF6B6B]">
              <svg className="w-12 h-12" viewBox="0 0 50 50" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M 10 30 C 5 25, 5 15, 15 15 C 20 10, 30 10, 35 15 C 45 15, 45 25, 40 30 C 45 35, 35 45, 25 40 C 15 45, 10 35, 10 30 Z" fill="#FF6B6B" fillOpacity="0.1" />
              </svg>
            </div>

            {/* Right Edge OK Hand Gesture Accent */}
            <div className="hidden lg:block absolute -right-12 bottom-12 z-30 text-amber-400 text-4xl filter drop-shadow-md">
              👌
            </div>

            {/* White Scheduling Table Container */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 text-slate-800 text-[8.5px] sm:text-[9.5px] w-full p-2.5 sm:p-3.5 font-sans">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse border border-slate-100">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold text-[8px]">
                      <th className="py-1.5 px-2 border-r border-slate-200 w-[24%] text-center">Schedule</th>
                      <th className="py-1.5 px-1 border-r border-slate-200 text-center w-[15%]">18</th>
                      <th className="py-1.5 px-1 border-r border-slate-200 text-center w-[15%]">19</th>
                      <th className="py-1.5 px-1 border-r border-slate-200 text-center w-[15%]">20</th>
                      <th className="py-1.5 px-1 border-r border-slate-200 text-center w-[15%] bg-amber-50 text-amber-900 font-bold">21</th>
                      <th className="py-1.5 px-1 text-center w-[16%]">22</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-[7.5px]">

                    {/* Row 1: Rattan and Wicker */}
                    <tr>
                      <td className="py-1 px-1.5 border-r border-slate-200 font-medium text-slate-700 bg-amber-50/50 flex items-center gap-1 truncate">
                        <span>🔧</span> <span className="truncate">Rattan and Wicker (Furnitu...)</span>
                      </td>
                      <td className="p-0.5 border-r border-slate-200">
                        <div className="bg-amber-200 text-amber-950 p-1 rounded font-semibold text-[7px] truncate">7:00 AM - 11:00 AM - Seni...</div>
                      </td>
                      <td className="p-0.5 border-r border-slate-200">
                        <div className="bg-sky-400 text-white p-1 rounded font-semibold text-[7px] truncate">7:00 AM - 11:00 AM - Furn...</div>
                      </td>
                      <td className="p-0.5 border-r border-slate-200">
                        <div className="bg-sky-500 text-white p-1 rounded font-semibold text-[7px] truncate">12:00 PM - 4:00 PM - Furni...</div>
                      </td>
                      <td className="p-0.5 border-r border-slate-200 bg-amber-50/30">
                        <div className="bg-amber-300 text-amber-950 p-1 rounded font-semibold text-[7px] truncate">12:00 PM - 11:59 PM - Sen...</div>
                      </td>
                      <td className="p-0.5">
                        <div className="bg-amber-200 text-amber-950 p-1 rounded font-semibold text-[7px] truncate">12:00 AM - 4:00 PM - Seni...</div>
                      </td>
                    </tr>

                    {/* Row 2: Ronnie Hart */}
                    <tr>
                      <td className="py-1 px-1.5 border-r border-slate-200 font-medium text-slate-700 bg-emerald-50/50 flex items-center gap-1 truncate">
                        <span className="w-3 h-3 rounded bg-amber-800 text-white text-[6px] font-bold flex items-center justify-center">R</span>
                        <span className="truncate">Ronnie Hart (Team Leader)</span>
                      </td>
                      <td className="p-0.5 border-r border-slate-200" />
                      <td className="p-0.5 border-r border-slate-200" />
                      <td className="p-0.5 border-r border-slate-200" />
                      <td className="p-0.5 border-r border-slate-200 bg-amber-50/30">
                        <div className="bg-amber-300 text-amber-950 p-1 rounded font-semibold text-[7px] truncate">12:00 PM - 4:00 PM - Seni...</div>
                      </td>
                      <td className="p-0.5">
                        <div className="bg-amber-200 text-amber-950 p-1 rounded font-semibold text-[7px] truncate">7:00 AM - 11:00 AM - Seni...</div>
                      </td>
                    </tr>

                    {/* Row 3: Scanner 1 */}
                    <tr>
                      <td className="py-1 px-1.5 border-r border-slate-200 font-medium text-slate-700 bg-slate-50 flex items-center gap-1 truncate">
                        <span>🔧</span> <span className="truncate">Scanner 1 (Scanner)</span>
                      </td>
                      <td className="p-0.5 border-r border-slate-200">
                        <div className="bg-teal-300 text-teal-950 p-1 rounded font-semibold text-[7px] truncate">7:00 AM - 11:00 AM - Scan...</div>
                      </td>
                      <td className="p-0.5 border-r border-slate-200" />
                      <td className="p-0.5 border-r border-slate-200" />
                      <td className="p-0.5 border-r border-slate-200 bg-amber-50/30" />
                      <td className="p-0.5" />
                    </tr>

                    {/* Row 4: Scanner 2 */}
                    <tr>
                      <td className="py-1 px-1.5 border-r border-slate-200 font-medium text-slate-700 bg-emerald-50/50 flex items-center gap-1 truncate">
                        <span>🔧</span> <span className="truncate">Scanner 2 (Scanner)</span>
                      </td>
                      <td className="p-0.5 border-r border-slate-200" />
                      <td className="p-0.5 border-r border-slate-200">
                        <div className="bg-teal-400 text-teal-950 p-1 rounded font-semibold text-[7px] truncate">12:00 PM - 4:00 PM - Scan...</div>
                      </td>
                      <td className="p-0.5 border-r border-slate-200">
                        <div className="bg-teal-400 text-teal-950 p-1 rounded font-semibold text-[7px] truncate">12:00 PM - 3:00 PM - Scan...</div>
                      </td>
                      <td className="p-0.5 border-r border-slate-200 bg-amber-50/30">
                        <div className="bg-teal-400 text-teal-950 p-1 rounded font-semibold text-[7px] truncate">6:00 AM - 3:00 PM - Scanner</div>
                      </td>
                      <td className="p-0.5">
                        <div className="bg-teal-400 text-teal-950 p-1 rounded font-semibold text-[7px] truncate">6:00 AM - 4:00 PM - Scanner</div>
                      </td>
                    </tr>

                    {/* Row 5: Sharlene Rhodes */}
                    <tr>
                      <td className="py-1 px-1.5 border-r border-slate-200 font-medium text-slate-700 bg-emerald-50/50 flex items-center gap-1 truncate">
                        <span className="w-3 h-3 rounded bg-purple-700 text-white text-[6px] font-bold flex items-center justify-center">S</span>
                        <span className="truncate">Sharlene Rhodes (Experien...)</span>
                      </td>
                      <td className="p-0.5 border-r border-slate-200">
                        <div className="bg-amber-300 text-amber-950 p-1 rounded font-semibold text-[7px] truncate">12:00 PM - 4:00 PM (2h15)...</div>
                      </td>
                      <td className="p-0.5 border-r border-slate-200">
                        <div className="bg-amber-300 text-amber-950 p-1 rounded font-semibold text-[7px] truncate">12:00 PM - 4:00 PM - Man...</div>
                      </td>
                      <td className="p-0.5 border-r border-slate-200">
                        <div className="bg-amber-300 text-amber-950 p-1 rounded font-semibold text-[7px] truncate">12:00 PM - 4:00 PM - Archi...</div>
                      </td>
                      <td className="p-0.5 border-r border-slate-200 bg-amber-50/30">
                        <div className="bg-amber-400 text-amber-950 p-1 rounded font-semibold text-[7px] truncate">Architect</div>
                      </td>
                      <td className="p-0.5" />
                    </tr>

                    {/* Row 6: Suman Oza */}
                    <tr>
                      <td className="py-1 px-1.5 border-r border-slate-200 font-medium text-slate-700 bg-slate-50 flex items-center gap-1 truncate">
                        <span className="w-3 h-3 rounded bg-slate-700 text-white text-[6px] font-bold flex items-center justify-center">S</span>
                        <span className="truncate">Suman Oza (Junior Architect)</span>
                      </td>
                      <td className="p-0.5 border-r border-slate-200" />
                      <td className="p-0.5 border-r border-slate-200">
                        <div className="bg-amber-300 text-amber-950 p-1 rounded font-semibold text-[7px] truncate">12:00 PM - 4:00 PM - Archi...</div>
                      </td>
                      <td className="p-0.5 border-r border-slate-200" />
                      <td className="p-0.5 border-r border-slate-200 bg-amber-50/30">
                        <div className="bg-amber-300 text-amber-950 p-1 rounded font-semibold text-[7px] truncate">12:00 PM - 4:00 PM - Archi...</div>
                      </td>
                      <td className="p-0.5">
                        <div className="bg-amber-300 text-amber-950 p-1 rounded font-semibold text-[7px] truncate">12:00 PM - 4:00 PM - Archi...</div>
                      </td>
                    </tr>

                    {/* Row 7: Table Saw */}
                    <tr>
                      <td className="py-1 px-1.5 border-r border-slate-200 font-medium text-slate-700 bg-amber-50/50 flex items-center gap-1 truncate">
                        <span>🔧</span> <span className="truncate">Table Saw (Furniture Tools)</span>
                      </td>
                      <td className="p-0.5 border-r border-slate-200">
                        <div className="bg-sky-400 text-white p-1 rounded font-semibold text-[7px] truncate">7:00 AM - 11:00 AM - Furn...</div>
                      </td>
                      <td className="p-0.5 border-r border-slate-200" />
                      <td className="p-0.5 border-r border-slate-200">
                        <div className="bg-sky-500 text-white p-1 rounded font-semibold text-[7px] truncate">12:00 PM - 4:00 PM - Furni...</div>
                      </td>
                      <td className="p-0.5 border-r border-slate-200 bg-amber-50/30" />
                      <td className="p-0.5">
                        <div className="bg-sky-400 text-white p-1 rounded font-semibold text-[7px] truncate">12:00 PM - 4:00 PM - Furni...</div>
                      </td>
                    </tr>

                    {/* Row 8: Toni Jimenez */}
                    <tr>
                      <td className="py-1 px-1.5 border-r border-slate-200 font-medium text-slate-700 bg-emerald-50/50 flex items-center gap-1 truncate">
                        <span className="w-3 h-3 rounded bg-rose-700 text-white text-[6px] font-bold flex items-center justify-center">T</span>
                        <span className="truncate">Toni Jimenez (Consultant)</span>
                      </td>
                      <td className="p-0.5 border-r border-slate-200">
                        <div className="bg-sky-400 text-white p-1 rounded font-semibold text-[7px] truncate">12:00 PM - 4:00 PM - Man...</div>
                      </td>
                      <td className="p-0.5 border-r border-slate-200">
                        <div className="bg-sky-400 text-white p-1 rounded font-semibold text-[7px] truncate">12:00 PM - 4:00 PM - Man...</div>
                      </td>
                      <td className="p-0.5 border-r border-slate-200">
                        <div className="bg-sky-400 text-white p-1 rounded font-semibold text-[7px] truncate">12:00 PM - 4:00 PM - Man...</div>
                      </td>
                      <td className="p-0.5 border-r border-slate-200 bg-amber-50/30">
                        <div className="bg-sky-500 text-white p-1 rounded font-semibold text-[7px] truncate">12:00 PM - 4:00 PM (1h30)...</div>
                      </td>
                      <td className="p-0.5">
                        <div className="bg-sky-400 text-white p-1 rounded font-semibold text-[7px] truncate">7:00 AM - 11:00 AM - Man...</div>
                      </td>
                    </tr>

                    {/* Row 9: Toolkit */}
                    <tr>
                      <td className="py-1 px-1.5 border-r border-slate-200 font-medium text-slate-700 bg-emerald-50/50 flex items-center gap-1 truncate">
                        <span>🔧</span> <span className="truncate">Toolkit</span>
                      </td>
                      <td className="p-0.5 border-r border-slate-200">
                        <div className="bg-purple-300 text-purple-950 p-1 rounded font-semibold text-[7px] truncate">12:00 PM - 4:00 PM</div>
                      </td>
                      <td className="p-0.5 border-r border-slate-200">
                        <div className="bg-purple-300 text-purple-950 p-1 rounded font-semibold text-[7px] truncate">12:00 PM - 4:00 PM - Reno...</div>
                      </td>
                      <td className="p-0.5 border-r border-slate-200">
                        <div className="bg-purple-300 text-purple-950 p-1 rounded font-semibold text-[7px] truncate">12:00 PM - 4:00 PM - Reno...</div>
                      </td>
                      <td className="p-0.5 border-r border-slate-200 bg-amber-50/30">
                        <div className="bg-purple-300 text-purple-950 p-1 rounded font-semibold text-[7px] truncate">12:00 PM - 4:00 PM - Offic...</div>
                      </td>
                      <td className="p-0.5">
                        <div className="bg-purple-300 text-purple-950 p-1 rounded font-semibold text-[7px] truncate">12:00 PM - 4:00 PM</div>
                      </td>
                    </tr>

                    {/* Row 10: Walter Horton */}
                    <tr>
                      <td className="py-1 px-1.5 border-r border-slate-200 font-medium text-slate-700 bg-slate-50 flex items-center gap-1 truncate">
                        <span className="w-3 h-3 rounded bg-amber-900 text-white text-[6px] font-bold flex items-center justify-center">W</span>
                        <span className="truncate">Walter Horton (Junior Arc...)</span>
                      </td>
                      <td className="p-0.5 border-r border-slate-200">
                        <div className="bg-purple-300 text-purple-950 p-1 rounded font-semibold text-[7px] truncate">7:00 AM - 11:00 AM - Juni...</div>
                      </td>
                      <td className="p-0.5 border-r border-slate-200">
                        <div className="bg-purple-300 text-purple-950 p-1 rounded font-semibold text-[7px] truncate">7:00 AM - 11:00 AM - Juni...</div>
                      </td>
                      <td className="p-0.5 border-r border-slate-200">
                        <div className="bg-purple-300 text-purple-950 p-1 rounded font-semibold text-[7px] truncate">7:00 AM - 11:00 AM - Juni...</div>
                      </td>
                      <td className="p-0.5 border-r border-slate-200 bg-amber-50/30">
                        <div className="bg-purple-300 text-purple-950 p-1 rounded font-semibold text-[7px] truncate">12:00 PM - 4:00 PM - Juni...</div>
                      </td>
                      <td className="p-0.5">
                        <div className="bg-purple-300 text-purple-950 p-1 rounded font-semibold text-[7px] truncate">7:00 AM - 11:00 AM - Juni...</div>
                      </td>
                    </tr>

                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </motion.div>

      </div>
    </section>
  );
}



/* ─────────────────────────────────────────────────────────────
   2. ARCHITECTURE FIRM: STATE OF THE ART QUOTATIONS SECTION
   Exact reproduction of Odoo's Architectural Proposal Documents Stack UI
   ───────────────────────────────────────────────────────────── */
export function ArchitectureStateOfTheArtQuotationsSection({ section }) {
  return (
    <section className="py-8 sm:py-14 lg:py-16 px-3 sm:px-8 lg:px-16 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto max-w-[1400px]">

        {/* CENTERED SECTION HEADER WITH HANDWRITTEN TITLE & UNDERLINE */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-8 sm:mb-12 text-center max-w-4xl mx-auto space-y-4"
        >
          <h2 className={`${caveat.className} text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight`}>
            <span>State of the art </span>
            <span className="relative inline-block px-1">
              quotations
              <svg
                className="absolute -bottom-2 left-0 w-full h-3 text-amber-400 overflow-visible"
                viewBox="0 0 100 12"
                fill="none"
              >
                <path
                  d="M 4 6 Q 50 1, 96 6"
                  stroke="#F59E0B"
                  strokeWidth="5"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </span>
          </h2>

          {/* 2 Subtext Paragraph Columns Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left max-w-5xl mx-auto pt-2">
            <p className="text-xl sm:text-2xl leading-relaxed text-gray-200">
              <strong className="text-white font-bold">Create custom quotations</strong> aligned with your project or client's profile, and integrate your 3D renderings, CAD files, blueprints, and more.
            </p>
            <p className="text-xl sm:text-2xl leading-relaxed text-gray-200">
              <strong className="text-white font-bold">Quotation templates</strong> save time thanks to a built-in set of architectural products and services.
            </p>
          </div>
        </motion.div>

        {/* EXACT PROPOSAL DOCUMENTS GRAPHIC IMAGE */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="w-full flex items-center justify-center pt-4"
        >
          <div className="relative w-full max-w-5xl flex justify-center items-center">

            {/* Left Smiley Icon Accent */}
            <div className="hidden lg:block absolute -left-6 top-10 z-30 text-amber-400 text-4xl sm:text-5xl transform -rotate-12 filter drop-shadow-md">
              😊
            </div>

            {/* Exact Odoo Proposal Image */}
            <img
              src="/odoo/industries/architecture/state-of-art-quotations.png"
              alt="State of the art quotations"
              className="w-full h-auto object-contain max-w-4xl rounded-xl shadow-2xl"
            />

          </div>
        </motion.div>

      </div>
    </section>
  );
}

